import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Search, Grid3x3, FileText, Star, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWorkspaceStore } from '../../store/workspace.store';
import { useAppTheme } from '../../hooks/useAppTheme';
export function MobileContentBrowser({ onClose }) {
    const navigate = useNavigate();
    const { isDark } = useAppTheme();
    const { currentWorkspace } = useWorkspaceStore();
    const [searchQuery, setSearchQuery] = useState('');
    // Collect all items with their types
    const allItems = useMemo(() => {
        if (!currentWorkspace)
            return { recent: [], boards: [], docs: [], dashboards: [], apps: [], forms: [] };
        const items = [];
        // Add boards
        (currentWorkspace.boards ?? []).forEach(board => {
            items.push({
                id: board.id,
                name: board.name,
                type: 'board',
                icon: Grid3x3,
                color: board.color,
                path: `/app/boards/${board.id}`,
                lastViewed: board.lastViewed ? new Date(board.lastViewed) : new Date(board.updatedAt),
                starred: board.starred
            });
        });
        // Add docs
        (currentWorkspace.docs ?? []).forEach(doc => {
            items.push({
                id: doc.id,
                name: doc.name,
                type: 'doc',
                icon: FileText,
                color: '#8B5CF6',
                path: `/app/ink/typst/${doc.ink_id}`,
                lastViewed: doc.lastViewed ? new Date(doc.lastViewed) : new Date(doc.updatedAt),
                starred: doc.starred
            });
        });
        // Add dashboards
        (currentWorkspace.dashboards ?? []).forEach(dashboard => {
            items.push({
                id: dashboard.id,
                name: dashboard.name,
                type: 'dashboard',
                icon: BarChart3,
                color: '#10B981',
                path: `/app/insights/dashboard/${dashboard.id}`,
                lastViewed: dashboard.lastViewed ? new Date(dashboard.lastViewed) : new Date(dashboard.updatedAt),
                starred: dashboard.starred
            });
        });
        // Organize by type
        const organized = {
            recent: items
                .sort((a, b) => b.lastViewed.getTime() - a.lastViewed.getTime())
                .slice(0, 5),
            boards: items.filter(i => i.type === 'board'),
            docs: items.filter(i => i.type === 'doc'),
            dashboards: items.filter(i => i.type === 'dashboard'),
            apps: [],
            forms: []
        };
        return organized;
    }, [currentWorkspace]);
    // Filter items based on search
    const filteredItems = useMemo(() => {
        if (!searchQuery)
            return allItems;
        const query = searchQuery.toLowerCase();
        return {
            recent: allItems.recent.filter(item => item.name.toLowerCase().includes(query)),
            boards: allItems.boards.filter(item => item.name.toLowerCase().includes(query)),
            docs: allItems.docs.filter(item => item.name.toLowerCase().includes(query)),
            dashboards: allItems.dashboards.filter(item => item.name.toLowerCase().includes(query)),
            apps: allItems.apps.filter(item => item.name?.toLowerCase().includes(query)),
            forms: allItems.forms.filter(item => item.name?.toLowerCase().includes(query))
        };
    }, [allItems, searchQuery]);
    const handleItemClick = (item) => {
        navigate(item.path);
        onClose?.();
    };
    const renderItem = (item) => {
        const Icon = item.icon;
        return (_jsxs("button", { onClick: () => handleItemClick(item), className: "w-full flex items-center gap-3 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-all text-left", children: [_jsx(Icon, { className: "w-4 h-4 flex-shrink-0", style: { color: item.color || 'currentColor' } }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("p", { className: "text-sm font-medium text-adaptive-primary truncate", children: item.name }), _jsx("p", { className: "text-xs text-adaptive-muted capitalize", children: item.type })] }), item.starred && (_jsx(Star, { className: "w-4 h-4 text-yellow-400 fill-yellow-400/30 flex-shrink-0" }))] }, item.id));
    };
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "relative", children: [_jsx(Search, { className: "absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-adaptive-muted" }), _jsx("input", { type: "text", placeholder: "Search items...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), className: "w-full pl-10 pr-4 py-2 rounded-lg border border-white/10 bg-white/5 text-adaptive-primary placeholder-adaptive-muted focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all" })] }), filteredItems.recent.length > 0 && (_jsxs(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { delay: 0.1 }, children: [_jsx("label", { className: "block text-xs font-semibold text-adaptive-muted uppercase mb-2", children: "Recent" }), _jsx("div", { className: "space-y-2", children: filteredItems.recent.map(item => renderItem(item)) })] })), filteredItems.boards.length > 0 && (_jsxs(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { delay: 0.15 }, children: [_jsxs("label", { className: "block text-xs font-semibold text-adaptive-muted uppercase mb-2", children: ["Boards (", filteredItems.boards.length, ")"] }), _jsx("div", { className: "space-y-2", children: filteredItems.boards.map(item => renderItem(item)) })] })), filteredItems.docs.length > 0 && (_jsxs(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { delay: 0.2 }, children: [_jsxs("label", { className: "block text-xs font-semibold text-adaptive-muted uppercase mb-2", children: ["Docs (", filteredItems.docs.length, ")"] }), _jsx("div", { className: "space-y-2", children: filteredItems.docs.map(item => renderItem(item)) })] })), filteredItems.dashboards.length > 0 && (_jsxs(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { delay: 0.25 }, children: [_jsxs("label", { className: "block text-xs font-semibold text-adaptive-muted uppercase mb-2", children: ["Dashboards (", filteredItems.dashboards.length, ")"] }), _jsx("div", { className: "space-y-2", children: filteredItems.dashboards.map(item => renderItem(item)) })] })), Object.values(filteredItems).every(arr => arr.length === 0) && (_jsx("div", { className: "py-8 text-center", children: _jsx("p", { className: "text-sm text-adaptive-muted", children: searchQuery ? `No items found for "${searchQuery}"` : 'No items in current workspace' }) }))] }));
}
